import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '@/api/client'
import { PageHeader } from '@/app/shell'
import { Badge, Button, Card, EmptyState, Skeleton } from '@/ui/primitives'
import { DataTable, type Coloana } from '@/ui/data-table'
import { eDatorie, lei } from '@/lib/money'

type Grup = {
  id: number; name: string; destination: string | null; hotel: string | null
  supplier: string | null; begin_date: string | null; end_date: string | null
  capacity: number | null; price_info: string | null; notes: string | null
  internal_notes: string | null; public_token: string | null; signups_open: boolean
  responsible: string | null; pax: number; locuri_ramase: number | null
}
type RezGrup = {
  id: number; number: string; partner: string | null; booking_customer: string | null
  status: string | null; begin_date: string | null; end_date: string | null
  pax: number; hotel: string | null; room_type: string | null
  total_value: number; currency: string | null; responsible: string | null; rest_ron: number
}
type Inscriere = {
  id: number; full_name: string; phone: string | null; email: string | null
  persons: number; notes: string | null; status: string; reservation_id: number | null
  created_at: string | null
}
type Detaliu = {
  group: Grup; eur_rate: number
  bani: { facturat_ron: number; incasat_ron: number; rest_ron: number; facturi: number }
  reservations: RezGrup[]; signups: Inscriere[]
}

const zi = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'

const TON: Record<string, 'good' | 'warn' | 'bad' | 'neutral'> = {
  nou: 'warn', contactat: 'neutral', confirmat: 'good', refuzat: 'bad',
}

export function GrupDetaliu() {
  const { id = '' } = useParams()
  const nav = useNavigate()
  const qc = useQueryClient()
  const [copiat, setCopiat] = useState(false)

  const { data, isPending, isError } = useQuery({
    queryKey: ['grup', id], queryFn: () => api.get<Detaliu>(`/groups/${id}`),
  })

  const schimba = useMutation({
    mutationFn: (corp: Record<string, unknown>) => api.put(`/groups/${id}`, corp),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['grup', id] }),
  })
  const marcheaza = useMutation({
    mutationFn: ({ sid, status }: { sid: number; status: string }) =>
      api.put(`/groups/${id}/inscrieri/${sid}`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['grup', id] }),
  })
  const stergeGrup = useMutation({
    mutationFn: () => api.del(`/groups/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['grupuri'] })
      qc.invalidateQueries({ queryKey: ['grupuri-sugestii'] })
      nav('/rezervari/grupuri')
    },
  })
  const stergeInscriere = useMutation({
    mutationFn: (sid: number) => api.del(`/groups/${id}/inscrieri/${sid}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['grup', id] }),
  })
  const scoate = useMutation({
    mutationFn: (rid: number) => api.del(`/groups/${id}/rezervari/${rid}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['grup', id] })
      qc.invalidateQueries({ queryKey: ['grupuri'] })
    },
  })

  if (isPending) return (<><PageHeader title="Grup" /><div className="px-8 py-6"><Card><Skeleton /></Card></div></>)
  if (isError || !data?.group) return (
    <><PageHeader title="Grup" /><div className="px-8 py-6"><Card>
      <EmptyState title="Nu am gasit grupul" /></Card></div></>)

  const g = data.group
  const link = g.public_token ? `${location.origin}/app/inscriere/${g.public_token}` : null

  const coloane: Coloana<RezGrup>[] = [
    { cheie: 'number', antet: 'Rezervare', sortabil: true, valoare: (r) => r.number,
      randare: (r) => <span className="tnum font-medium">#{r.number}</span> },
    { cheie: 'client', antet: 'Client', sortabil: true, valoare: (r) => r.partner ?? '',
      randare: (r) => <span className="block max-w-[260px] truncate">{r.partner ?? r.booking_customer ?? '—'}</span> },
    { cheie: 'pax', antet: 'Pers.', sortabil: true, aliniat: 'dreapta', valoare: (r) => r.pax,
      randare: (r) => <span className="tnum">{r.pax || '—'}</span> },
    { cheie: 'camera', antet: 'Cazare',
      randare: (r) => <span className="block max-w-[220px] truncate text-muted text-[13px]">
        {[r.hotel, r.room_type].filter(Boolean).join(' · ') || '—'}</span> },
    { cheie: 'perioada', antet: 'Perioada', sortabil: true, valoare: (r) => r.begin_date ?? '',
      randare: (r) => <span className="tnum text-[13px] text-muted whitespace-nowrap">{zi(r.begin_date)} – {zi(r.end_date)}</span> },
    { cheie: 'rest', antet: 'Rest', sortabil: true, aliniat: 'dreapta', valoare: (r) => r.rest_ron,
      randare: (r) => (eDatorie(r.rest_ron)
        ? <span className="tnum text-bad">{lei(r.rest_ron)} lei</span>
        : <Badge tone="good">achitat</Badge>) },
    { cheie: 'stare', antet: 'Stare', sortabil: true, valoare: (r) => r.status ?? '',
      randare: (r) => <span className="text-[13px] text-muted">{r.status ?? '—'}</span> },
    { cheie: 'scoate', antet: '',
      randare: (r) => (
        <button className="text-[13px] text-muted hover:text-bad"
          onClick={(e) => { e.stopPropagation(); scoate.mutate(r.id) }}>scoate</button>
      ) },
  ]

  return (
    <>
      <PageHeader
        title={g.name}
        count={`${g.pax} persoane · ${data.reservations.length} rezervari`}
        actions={<Link to="/rezervari/grupuri" className="text-[14px] text-muted hover:text-ink">← Toate grupurile</Link>}
      />
      <div className="px-8 py-6 grid gap-6 lg:grid-cols-[1fr_340px] items-start">
        <div className="space-y-6 min-w-0">
          <DataTable
            randuri={data.reservations} coloane={coloane} cheie={(r) => r.id}
            sortImplicit={{ cheie: 'client', sens: 'asc' }}
            peRand={(r) => nav(`/rezervari/${r.id}`)}
            gol={{ titlu: 'Nicio rezervare in grup',
                   indiciu: 'Adauga rezervari din lista de rezervari sau din sugestii.' }}
          />

          <div>
            <h2 className="text-[13px] font-semibold tracking-[0.08em] uppercase text-muted mb-2">
              Inscrieri de pe link ({data.signups.length})
            </h2>
            <Card className="overflow-hidden">
              {data.signups.length === 0 ? (
                <EmptyState title="Nicio inscriere inca"
                  hint="Trimite linkul public grupului; inscrierile apar aici." />
              ) : (
                <table className="w-full text-[14px]">
                  <tbody>
                    {data.signups.map((s) => (
                      <tr key={s.id} className="border-t border-line first:border-0">
                        <td className="px-5 py-3">
                          <span className="font-medium">{s.full_name}</span>
                          <span className="block text-[12.5px] text-muted">
                            {[s.phone, s.email].filter(Boolean).join(' · ') || 'fara contact'}
                          </span>
                          {s.notes && <span className="block text-[12.5px] text-muted italic">{s.notes}</span>}
                        </td>
                        <td className="px-5 py-3 tnum text-[13px] text-muted">{s.persons} pers.</td>
                        <td className="px-5 py-3 tnum text-[13px] text-muted">{zi(s.created_at)}</td>
                        <td className="px-5 py-3"><Badge tone={TON[s.status] ?? 'neutral'}>{s.status}</Badge></td>
                        <td className="px-5 py-3 text-right whitespace-nowrap">
                          {s.phone && (
                            <a href={`tel:${s.phone}`} className="text-[13px] text-accent-ink hover:underline mr-3">suna</a>
                          )}
                          {s.status !== 'confirmat' && (
                            <button className="text-[13px] text-accent-ink hover:underline mr-3"
                              onClick={() => marcheaza.mutate({ sid: s.id, status: s.status === 'nou' ? 'contactat' : 'confirmat' })}>
                              {s.status === 'nou' ? 'am sunat' : 'confirma'}
                            </button>
                          )}
                          {s.status !== 'refuzat' && (
                            <button className="text-[13px] text-muted hover:text-bad mr-3"
                              onClick={() => marcheaza.mutate({ sid: s.id, status: 'refuzat' })}>renunta</button>
                          )}
                          <button className="text-[13px] text-muted hover:text-bad"
                            onClick={() => stergeInscriere.mutate(s.id)}>sterge</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          </div>
        </div>

        <div className="space-y-4">
          <Card className="p-5 space-y-2.5">
            <p className="text-[12.5px] text-muted">Calatoria</p>
            <p className="text-[14.5px]">{[g.destination, g.hotel].filter(Boolean).join(' · ') || '—'}</p>
            <p className="text-[13.5px] text-muted tnum">{zi(g.begin_date)} – {zi(g.end_date)}</p>
            {g.supplier && <p className="text-[13px] text-muted">furnizor: {g.supplier}</p>}
            {g.capacity && (
              <p className="text-[13.5px] tnum">
                {g.pax} din {g.capacity} locuri
                <span className="text-muted"> · {g.locuri_ramase} libere</span>
              </p>
            )}
            {g.responsible && <p className="text-[13px] text-muted">agent: {g.responsible}</p>}
          </Card>

          <Card className="p-5 space-y-2.5">
            <p className="text-[12.5px] text-muted">Bani pe grup ({data.bani.facturi} facturi)</p>
            <div className="flex justify-between text-[14px]">
              <span className="text-muted">Facturat</span><span className="tnum">{lei(data.bani.facturat_ron)} lei</span>
            </div>
            <div className="flex justify-between text-[14px]">
              <span className="text-muted">Incasat</span><span className="tnum text-good">{lei(data.bani.incasat_ron)} lei</span>
            </div>
            <div className="flex justify-between text-[14px] pt-2.5 border-t border-line">
              <span className="text-muted">Ramas</span>
              <span className={'tnum ' + (eDatorie(data.bani.rest_ron) ? 'text-bad' : '')}>
                {lei(data.bani.rest_ron)} lei
              </span>
            </div>
          </Card>

          <Card className="p-5 space-y-3">
            <p className="text-[12.5px] text-muted">Link de inscriere</p>
            {link ? (
              <>
                <p className="text-[12.5px] break-all bg-page rounded-lg p-2.5">{link}</p>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => {
                    navigator.clipboard?.writeText(link)
                    setCopiat(true); setTimeout(() => setCopiat(false), 2000)
                  }}>{copiat ? 'Copiat' : 'Copiaza linkul'}</Button>
                  <a href={`https://wa.me/?text=${encodeURIComponent(`${g.name}\n${link}`)}`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center h-9 px-3.5 rounded-[var(--radius-control)] border border-line-strong text-[14px] hover:bg-accent-soft hover:border-accent">
                    Trimite pe WhatsApp
                  </a>
                </div>
                <label className="flex items-center gap-2 text-[13.5px] pt-1">
                  <input type="checkbox" checked={g.signups_open}
                    onChange={(e) => schimba.mutate({ signups_open: e.target.checked })} />
                  Inscrierile sunt deschise
                </label>
              </>
            ) : <p className="text-[13.5px] text-muted">Grupul nu are link public.</p>}
          </Card>

          <Card className="p-5">
            <p className="text-[12.5px] text-muted">Sterge grupul</p>
            <p className="mt-1 text-[13px] text-muted">
              Rezervarile raman neatinse, doar nu mai fac parte din grup.
            </p>
            <button
              onClick={() => { if (confirm(`Stergi grupul „${g.name}"? Rezervarile raman.`)) stergeGrup.mutate() }}
              disabled={stergeGrup.isPending}
              className="mt-3 h-9 px-3.5 rounded-[var(--radius-control)] border border-line-strong text-[14px] text-bad hover:bg-bad-soft disabled:opacity-50">
              {stergeGrup.isPending ? 'Se sterge…' : 'Sterge grupul'}
            </button>
          </Card>
        </div>
      </div>
    </>
  )
}
